import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, Typography, Stack, Link, Grid } from '@mui/material';
import useEventContext from '@/contexts/event/useEventContext';
import 'dayjs/locale/es-mx';
import Image from '@/components/Image';
import ResumeInfo from '@/components/ResumeInfo';
import useMobile from '../../../../hooks/useMobile';
import {
  formatUTCDate,
  formatDateRange,
  formatLocalDate,
} from '@/locales/dates';
import CustomLinkBanner from '@/sections/custom-links/CustomLinkBanner';

const GOOGLE_MAPS_KEY = process.env.NEXT_PUBLIC_GOOGLE_MAPS_KEY;

function EventSummary({ onReadMore }) {
  const { t } = useTranslation();
  const [calendarStartDate, setCalendarStartDate] = useState(null);
  const [calendarEndDate, setCalendarEndDate] = useState(null);
  const {
    event: {
      event: { name, banner, start_date, end_date, use_seats },
      location: { venue, latitude, longitude, city, state } = {
        venue: '',
        address: '',
      },
    },
  } = useEventContext();
  const { isMobile } = useMobile();

  const showViewMore = (use_seats && onReadMore) || isMobile;

  useEffect(() => {
    setCalendarStartDate(formatUTCDate(start_date, 'YYYYMMDDTHHmmss'));

    setCalendarEndDate(formatUTCDate(end_date, 'YYYYMMDDTHHmmss'));
  }, []);

  return (
    <>
      <Stack spacing={2}>
        <>
          {isMobile ? (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '20px',
                overflow: 'hidden',
              }}
            >
              <Image
                sx={{ borderRadius: '16px', width: '343px', height: '125px' }}
                src={banner}
              />
            </Box>
          ) : (
            <Box
              sx={{
                width: '100%',
                display: 'flex',
                justifyContent: 'center',
                alignItems: 'center',
                borderRadius: '16px',
                overflow: 'hidden',
              }}
            >
              <Image src={banner} ratio="480/180" />
            </Box>
          )}
          {isMobile && <CustomLinkBanner />}
        </>
      </Stack>
      <Box
        sx={{
          width: '100%',
          mt: 2,
        }}
      >
        <Stack alignItems="center" direction="row">
          <Typography variant="h5" component="div">
            {name}
          </Typography>
          <Box sx={{ flex: 1 }}></Box>
          {showViewMore && (
            <Link
              variant="caption"
              sx={{
                'textDecoration': 'underline',
                '&:hover': {
                  cursor: 'pointer',
                },
                'color': (theme) => theme.palette.secondary.main,
              }}
              onClick={onReadMore}
            >
              {t('checkout.global.readMore')}
            </Link>
          )}
        </Stack>
      </Box>
      <Box
        sx={{
          width: '100%',
        }}
      >
        <Grid container spacing={1} sx={{ height: '100%', marginTop: '0px' }}>
          <Grid item xs={12} md={6}>
            <ResumeInfo
              title={formatLocalDate(start_date)}
              subtitle={formatDateRange(start_date, end_date)}
              iconify="ic:baseline-calendar-today"
              url={`http://www.google.com/calendar/render?action=TEMPLATE&text=${name}&dates=${calendarStartDate}/${calendarEndDate}&location=${venue}`}
              sx={{
                '& h6': {
                  textTransform: 'capitalize',
                },
              }}
            />
          </Grid>
          <Grid item md={6}>
            {venue !== 'Online' && (
              <ResumeInfo
                title={venue}
                subtitle={`${city}, ${state}`}
                url={`https://www.google.com/maps/embed/v1/place?key=${GOOGLE_MAPS_KEY}&q=${latitude},${longitude}`}
                iconify="ic:baseline-place"
              />
            )}
            {venue === 'Online' && (
              <ResumeInfo
                title={venue}
                url={`http://www.google.com/calendar/render?action=TEMPLATE&text=${name}&dates=${calendarStartDate}/${calendarEndDate}&location=${venue}`}
                iconify="ic:baseline-place"
              />
            )}
          </Grid>
        </Grid>
      </Box>
    </>
  );
}

export default EventSummary;
